// Sentence Structure — word order, ser vs estar, questions, negation,
// prepositions, contractions. Ported from the legacy views.structure; uses
// the shared renderGuideSections helper.
import { renderGuideSections } from "../ui/guideSections.js";

// Hand-drawn flat SVG of the S–V–O flow — accurate and editable because it's
// vector code, not a generated image.
const WORD_ORDER_FIGURE = `
  <div class="bg-white border border-sage-100 rounded-2xl p-4 sm:p-5 mb-6">
    <p class="text-xs font-semibold uppercase tracking-wide text-sage-400 mb-2">Word order at a glance</p>
    <svg viewBox="0 0 660 230" class="w-full h-auto max-w-2xl mx-auto" role="img" aria-label="Italian word order: Subject then Verb then Object">
      <defs>
        <marker id="wo-arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0 0 L8 3 L0 6 z" fill="#94a3b8"></path>
        </marker>
      </defs>
      <rect x="30" y="28" width="160" height="58" rx="14" fill="#d1fae5"></rect>
      <text x="110" y="63" text-anchor="middle" font-weight="700" fill="#065f46" font-size="17">SUBJECT</text>
      <rect x="250" y="28" width="160" height="58" rx="14" fill="#fef3c7"></rect>
      <text x="330" y="63" text-anchor="middle" font-weight="700" fill="#92400e" font-size="17">VERB</text>
      <rect x="470" y="28" width="160" height="58" rx="14" fill="#e0f2fe"></rect>
      <text x="550" y="63" text-anchor="middle" font-weight="700" fill="#075985" font-size="17">OBJECT</text>
      <path d="M190 57 H250" stroke="#94a3b8" stroke-width="3" marker-end="url(#wo-arrow)"></path>
      <path d="M410 57 H470" stroke="#94a3b8" stroke-width="3" marker-end="url(#wo-arrow)"></path>
      <text x="110" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">Maria</text>
      <text x="330" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">mangia</text>
      <text x="550" y="138" text-anchor="middle" fill="#1f2937" font-size="20" font-weight="600">le mele</text>
      <text x="110" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">María</text>
      <text x="330" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">eats</text>
      <text x="550" y="160" text-anchor="middle" fill="#9ca3af" font-size="12">apples</text>
      <text x="330" y="206" text-anchor="middle" fill="#6b7280" font-size="13">Default order is Subject → Verb → Object — the same as English.</text>
    </svg>
    <p class="text-sage-600 text-sm mt-2">Because the verb ending already shows <i>who</i> is acting, Italian can shuffle parts for emphasis — but <b>S–V–O</b> is the safe default. (Adjectives, though, go <b>after</b> the noun: <i>una casa <b>bianca</b></i>.)</p>
  </div>`;

export function structureView(app) {
  renderGuideSections(
    app,
    "Sentence Structure",
    "How Italian sentences are built — word order, the verbs for \"to be\", questions and negation, prepositions, and the all-important articulated prepositions.",
    [
      {
        title: "1. The big picture — four shifts from English",
        body: `
          <p class="text-sage-700 mb-2">Good news for an English speaker: the basic order is the same — <b>subject → verb → object</b>. The differences come down to a short list, and most of them make Italian <i>simpler</i>, not harder.</p>
          <ol class="text-sage-700 text-sm space-y-1 list-decimal pl-5">
            <li>The subject is usually <b>dropped</b> — the verb ending already says who.</li>
            <li>Adjectives usually go <b>after</b> the noun.</li>
            <li>There's <b>no "do/does"</b> helper for questions or negatives — the plain verb does the job.</li>
            <li>Questions are made by <b>intonation</b> alone — no reordering and no special punctuation.</li>
          </ol>
          <p class="text-sage-700 text-sm mt-3"><b>Don't translate "do":</b> <i>Lavori?</i> already means "Do you work?" — there's no word for the question/negative <i>do</i>. The verb alone asks: <i>Parla inglese?</i> = "Does she speak English?"</p>
        `,
      },
      {
        title: "2. Basic word order",
        body: `
          <p class="text-sage-700 mb-2">Default order is <b>Subject – Verb – Object</b>, but it's looser than in English because verb endings already show who's doing what.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>(Io) mangio le mele.</i> — I eat apples. (Subject often dropped.)</li>
            <li><i>Mi piace il caffè.</i> — Literally: "Coffee is pleasing to me." The grammatical subject (<i>il caffè</i>) comes after the verb.</li>
            <li>Adjectives go <b>after</b> the noun: <i>una casa <b>bianca</b></i>.</li>
            <li>Object pronouns go <b>before</b> conjugated verbs: <i>Lo vedo.</i> — I see him/it.</li>
            <li>With infinitives or gerunds, pronouns can attach to the end: <i>voglio vederlo</i>, <i>sto guardandolo</i>.</li>
          </ul>
        `,
      },
      {
        title: "3. Essere vs Stare (and avere) for \"to be\"",
        body: `
          <p class="text-sage-700 mb-2">Italian mostly uses <b>essere</b> for "to be", with <b>stare</b> reserved for health and set phrases:</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Use ESSERE for…</th><th class="py-2 px-3">Use STARE for…</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Identity / profession (<i>sono medico</i>)</td><td class="py-2 px-3">Health (<i>sto bene / sto male</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Origin / nationality (<i>è di Roma</i>)</td><td class="py-2 px-3">"How are you?" (<i>come stai?</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Traits & feelings (<i>è alto, sono stanco</i>)</td><td class="py-2 px-3">Progressive: stare + gerundio (<i>sto mangiando</i>)</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3">Time / dates (<i>sono le tre</i>)</td><td class="py-2 px-3">Set phrases (<i>stai attento, stare zitto</i>)</td></tr>
              <tr><td class="py-2 pr-3">Location (<i>è a casa</i>)</td><td class="py-2 px-3">"Stay" (<i>sto a casa stasera</i>)</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><b>Heads-up:</b> many English "to be" phrases use <b>avere</b> in Italian — <i>ho fame</i> (I'm hungry), <i>ho sete</i> (I'm thirsty), <i>ho freddo</i> (I'm cold), <i>ho vent'anni</i> (I'm 20), <i>ho paura</i> (I'm afraid).</p>
        `,
      },
      {
        title: "4. Questions",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Yes/no questions just flip intonation — no extra word, no reordering. Written: <i>Parli italiano?</i></li>
            <li>Italian uses <b>no special punctuation</b> — just a normal <i>?</i> at the end.</li>
            <li><b>Question words:</b> chi (who), che / che cosa / cosa (what), quando, dove, come, quale, quanto, perché.</li>
            <li>Examples: <i>Cosa fai? Dove abiti? Quanti anni hai? Perché non vieni?</i></li>
            <li><b>che/cosa</b> vs <b>quale</b>: use <i>che cosa</i> to ask what something is; <i>quale</i> to pick from options. <i>Che cos'è questo?</i> vs <i>Quale preferisci?</i></li>
          </ul>
        `,
      },
      {
        title: "5. Negation",
        body: `
          <p class="text-sage-700 mb-2">Put <b>non</b> directly before the verb: <i>Non parlo francese.</i></p>
          <p class="text-sage-700 mb-2">Unlike English, Italian uses <b>double negatives</b> — they don't cancel each other out:</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><i>Non ho <b>niente</b>.</i> — I don't have anything.</li>
            <li><i>Non conosco <b>nessuno</b>.</i> — I don't know anyone.</li>
            <li><i>Non vado <b>mai</b></i> OR <i><b>Mai</b> vado</i> — I never go. (Either is fine.)</li>
            <li>If the negative word comes <b>before</b> the verb, drop the <i>non</i>; if it comes after, keep it: <i><b>Nessuno</b> viene</i> vs <i><b>Non</b> viene <b>nessuno</b></i>.</li>
          </ul>
        `,
      },
      {
        title: "6. Common prepositions",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>a</b> — to, at, in (cities): <i>vado <b>a</b> Roma, <b>alle</b> due</i>.</li>
            <li><b>di</b> — of, from; possession (<i>il libro <b>di</b> Giovanni</i>).</li>
            <li><b>da</b> — from, by, since, and "at someone's place" (<i>vengo <b>da</b> Roma, <b>dal</b> medico</i>).</li>
            <li><b>in</b> — in, to (countries, regions, transport): <i><b>in</b> Italia, <b>in</b> treno</i>.</li>
            <li><b>con / senza</b> — with / without.</li>
            <li><b>per</b> — for, through, in order to (goal/cause).</li>
            <li><b>su</b> — on, about; <b>tra / fra</b> — between, among, in (time).</li>
          </ul>
          <p class="text-sage-700 text-sm mt-3"><b>No "personal a":</b> unlike Spanish, Italian does <b>not</b> mark a personal direct object — <i>Vedo Maria</i> (not <i>Vedo a Maria</i>).</p>
        `,
      },
      {
        title: "7. Articulated prepositions — many!",
        body: `
          <p class="text-sage-700 mb-2">Italian has a whole system of contractions: <b>di, a, da, in, su</b> fuse with the definite article (<i>con</i> optionally → <i>col</i>).</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>a + il → al</b>: <i>Vado <b>al</b> mercato.</i> · <b>a + lo → allo</b>, <b>a + la → alla</b>, <b>a + i → ai</b>, <b>a + gli → agli</b>, <b>a + le → alle</b>.</li>
            <li><b>di + il → del</b>: <i>La macchina <b>del</b> professore.</i> · <b>da + il → dal</b>, <b>in + il → nel</b>, <b>su + il → sul</b>.</li>
            <li>Examples: <i><b>nello</b> zaino, <b>sulla</b> tavola, <b>dagli</b> amici, <b>dell'</b>amico</i>.</li>
          </ul>
          <p class="text-sage-700 text-sm mt-2">See the Articles guide for the full grid of every preposition × article combination.</p>
        `,
      },
    ],
    WORD_ORDER_FIGURE
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
